import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface EstimatorSettings {
  default_labor_markup_pct: number;
  default_material_markup_pct: number;
  default_tax_rate: number;
  default_sales_tax_rate: number;
  estimate_notes_template: string;
}

const DEFAULTS: EstimatorSettings = {
  default_labor_markup_pct: 35,
  default_material_markup_pct: 20,
  default_tax_rate: 0,
  default_sales_tax_rate: 8.25,
  estimate_notes_template: "",
};

export function useEstimatorSettings() {
  return useQuery<EstimatorSettings>({
    queryKey: ["estimator-settings"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return DEFAULTS;

      const { data, error } = await supabase
        .from("company_settings")
        .select("default_labor_markup_pct, default_material_markup_pct, default_tax_rate, default_sales_tax_rate, estimate_notes_template")
        .eq("company_id", companyId)
        .maybeSingle();

      if (error || !data) return DEFAULTS;
      return {
        default_labor_markup_pct: (data as any).default_labor_markup_pct ?? DEFAULTS.default_labor_markup_pct,
        default_material_markup_pct: (data as any).default_material_markup_pct ?? DEFAULTS.default_material_markup_pct,
        default_tax_rate: (data as any).default_tax_rate ?? DEFAULTS.default_tax_rate,
        default_sales_tax_rate: (data as any).default_sales_tax_rate ?? DEFAULTS.default_sales_tax_rate,
        estimate_notes_template: (data as any).estimate_notes_template ?? DEFAULTS.estimate_notes_template,
      };
    },
    staleTime: 5 * 60_000,
  });
}

export function useUpdateEstimatorSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<EstimatorSettings>) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const { error } = await supabase
        .from("company_settings")
        .update(input as any)
        .eq("company_id", companyId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimator-settings"] }),
  });
}
